import { useRef, useState, type ChangeEvent } from 'react';
import { useUploadQuestionnaireMedia, useDeleteQuestionnaireMedia } from '../../data/queries';
import { useBabyCaps } from '../../data/permissions';
import { useEncryption } from '../../crypto/EncryptionProvider';
import { useFileDrop } from '../../hooks/useFileDrop';
import { MediaEngageTile } from '../engagement/MediaEngageTile';
import { MediaLightbox } from '../media/MediaLightbox';
import { bytesToBlob } from '../../crypto/crypto';
import { prepareImageUpload } from '../../crypto/uploadImage';
import { extractVideoPosterFrame } from '../../crypto/videoPoster';
import type { MediaRow } from '../../data/dto';
import { useT } from '../../i18n';

interface Props {
  babyId: string;
  code: string;
  photos: MediaRow[];
  onOpenMedia?: () => void;
}

/**
 * Photo/video grid for a questionnaire response — same flow as the activity /
 * milestone galleries: encrypted upload + compression, per-photo comments &
 * reactions (via MediaEngageTile / MediaLightbox, entityType 'media'), and
 * delete. Mirrors ActivityDonePhotos.
 */
export function QuestionnaireMedia({ babyId, code, photos, onOpenMedia }: Props) {
  const enc = useEncryption();
  const caps = useBabyCaps(babyId);
  const upload = useUploadQuestionnaireMedia();
  const del = useDeleteQuestionnaireMedia();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const t = useT();
  const canDecrypt = enc.status === 'ready';

  const pick = async () => {
    setError(null);
    const ready = await enc.ensureReady();
    if (!ready) return;
    inputRef.current?.click();
  };

  const processFiles = async (files: File[]) => {
    if (files.length === 0) return;
    setError(null);
    const ready = await enc.ensureReady();
    if (!ready) return;

    setBusy(true);
    try {
      await Promise.all(
        files.map(async (file) => {
          const isVideo = file.type.startsWith('video/') || /\.(mp4|mov|m4v|webm|ogv|avi|mkv|3gp)$/i.test(file.name);
          const form = new FormData();

          if (isVideo) {
            if (file.size > 50 * 1024 * 1024) {
              throw new Error(`Video "${file.name}" exceeds the 50MB limit.`);
            }
            const { bytes, encrypted } = enc.encryptForUpload(new Uint8Array(await file.arrayBuffer()));
            form.append('file', bytesToBlob(bytes, 'application/octet-stream'), `${file.name}.bin`);
            const poster = await extractVideoPosterFrame(file);
            if (poster) {
              const posterBytes = new Uint8Array(await poster.arrayBuffer());
              const thumbBytes = encrypted ? enc.encryptForUpload(posterBytes).bytes : posterBytes;
              form.append('thumbnail', bytesToBlob(thumbBytes, 'application/octet-stream'), `${file.name}.thumb.bin`);
            }
            form.append('is_encrypted', encrypted ? '1' : '0');
            form.append('mime_type', file.type || 'video/mp4');
          } else {
            if (file.size > 20 * 1024 * 1024) {
              throw new Error(`Photo "${file.name}" exceeds the 20MB limit.`);
            }
            const parts = await prepareImageUpload(file, enc.encryptForUpload);
            form.append('file', parts.fileBlob, `${file.name}.bin`);
            if (parts.thumbBlob) form.append('thumbnail', parts.thumbBlob, `${file.name}.thumb.bin`);
            form.append('is_encrypted', parts.encrypted ? '1' : '0');
            form.append('mime_type', parts.mime);
            if (parts.width > 0) form.append('width_px', String(parts.width));
            if (parts.height > 0) form.append('height_px', String(parts.height));
          }

          await upload.mutateAsync({ babyId, code, form });
        })
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : t('questionnaire.uploadFailed', 'Upload failed.'));
    } finally {
      setBusy(false);
    }
  };

  const onFiles = (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = '';
    void processFiles(files);
  };

  const { isDraggingOver, dropProps } = useFileDrop({
    onDropFiles: processFiles,
    disabled: !caps.canUploadMedia || busy,
  });

  return (
    <div>
      <label className="mb-2 block text-[11px] font-semibold uppercase tracking-wider text-ink-3">
        {t('questionnaire.photos', 'Photos & videos')}
      </label>
      <div
        {...dropProps}
        className={[
          'grid grid-cols-3 gap-2 rounded-xl p-1.5 -m-1.5 transition-all duration-150',
          isDraggingOver ? 'bg-accent/10 ring-2 ring-accent ring-dashed' : '',
        ].join(' ')}
      >
        {photos.map((m, i) => (
          <MediaEngageTile
            key={m.id}
            media={m}
            babyId={babyId}
            canDecrypt={canDecrypt}
            decrypt={enc.decrypt}
            onOpen={() => setLightboxIndex(i)}
            onUnlock={() => void enc.ensureReady()}
            onDelete={caps.canWrite ? () => del.mutate({ mediaId: m.id, babyId }) : undefined}
          />
        ))}
        {caps.canUploadMedia && (
          <button
            type="button"
            onClick={pick}
            disabled={busy}
            className={[
              'flex aspect-square w-full flex-col items-center justify-center gap-1.5 rounded-xl text-accent-ink disabled:opacity-60 cursor-pointer transition-all',
              isDraggingOver ? 'scale-105' : '',
            ].join(' ')}
            style={{
              border: isDraggingOver
                ? '2px dashed var(--color-accent)'
                : '1.5px dashed color-mix(in oklch, var(--color-accent) 35%, var(--color-line))',
              background: isDraggingOver
                ? 'color-mix(in oklch, var(--color-accent) 15%, var(--color-bg))'
                : 'color-mix(in oklch, var(--color-accent) 5%, var(--color-bg))',
            }}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="6" width="18" height="14" rx="2.5" />
              <circle cx="12" cy="13" r="3.5" />
              <path d="M8 6l1.5-2h5L16 6" />
            </svg>
            <span className="text-[10px] font-semibold uppercase tracking-wider">
              {busy ? t('common.saving', 'Saving…') : isDraggingOver ? t('questionnaire.dropPhoto', 'Drop') : photos.length === 0 ? t('questionnaire.addPhoto', 'Add photo') : t('questionnaire.addPhotoShort', 'Add')}
            </span>
          </button>
        )}
      </div>
      {error && <p className="mt-2 text-[12px] leading-snug text-[#7E3320]">{error}</p>}
      <input ref={inputRef} type="file" accept="image/*,video/*" multiple onChange={onFiles} className="hidden" />

      {lightboxIndex !== null && photos[lightboxIndex] && (
        <MediaLightbox
          items={photos}
          index={lightboxIndex}
          onIndex={setLightboxIndex}
          onClose={() => setLightboxIndex(null)}
          canDecrypt={canDecrypt}
          decrypt={enc.decrypt}
          babyId={babyId}
          onOpenMedia={onOpenMedia}
          onDelete={caps.canWrite ? (m) => del.mutateAsync({ mediaId: m.id, babyId }) : undefined}
        />
      )}
    </div>
  );
}
